// Opening and printing a task file for someone with no Google account
// (assistants). The file comes through the Worker (GET /api/v1/task-files/...)
// with the bearer token, then shows from a blob URL. PDFs and images display
// in the browser's own viewer (which has Print); anything else (Word, Excel)
// cannot be shown, so it downloads and the caller says so.
import { session } from './session';
import { authedGetBlob } from './apiClient';

export type TaskFileKind = 'attachment' | 'instructions' | 'report' | 'filed';

export function isAssistant(): boolean {
  return session.value.me?.role === 'ASSISTANT';
}

export function taskFilePath(kind: TaskFileKind, id: number | string): string {
  return `/api/v1/task-files/${kind}/${id}/content`;
}

export type FileOutcome = 'shown' | 'printed' | 'downloaded';

function viewable(type: string): boolean {
  return type === 'application/pdf' || type.startsWith('image/');
}

function download(url: string, name: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function printInHiddenFrame(src: string, isImage: boolean): void {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  frame.setAttribute('aria-hidden', 'true');
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch { /* the viewer refused: the file is still open in the frame */ }
    setTimeout(() => frame.remove(), 60_000);
  };
  if (isImage) {
    // a small print view: the image alone, scaled to the page
    const html = `<!doctype html><title>Print</title><style>@page{margin:12mm}body{margin:0}img{max-width:100%;max-height:100vh}</style><img src="${src}">`;
    frame.srcdoc = html;
  } else {
    frame.src = src;
  }
  document.body.appendChild(frame);
}

/** Open (or print) one task file. Opens the tab first so pop-up blockers allow it. */
export async function openTaskFile(
  kind: TaskFileKind, id: number | string, opts: { print?: boolean; name?: string } = {},
): Promise<FileOutcome> {
  const win = opts.print ? null : window.open('', '_blank');
  try {
    const { blob, contentType } = await authedGetBlob(taskFilePath(kind, id));
    const url = URL.createObjectURL(blob);
    const type = contentType.split(';')[0].trim().toLowerCase();
    if (!viewable(type)) {
      win?.close();
      download(url, opts.name || 'file');
      return 'downloaded';
    }
    if (opts.print) {
      printInHiddenFrame(url, type.startsWith('image/'));
      return 'printed';
    }
    if (win) win.location.href = url;
    else window.open(url, '_blank');
    return 'shown';
  } catch (err) {
    win?.close();
    throw err;
  }
}

/** The Drive file id inside a Drive / Docs link, or null. Matches src/task_files.js. */
export function driveIdOf(raw: string): string | null {
  let u: URL;
  try { u = new URL(raw); } catch { return null; }
  if (u.protocol !== 'https:' || (u.hostname !== 'drive.google.com' && u.hostname !== 'docs.google.com')) return null;
  const m = /\/(?:file|document|spreadsheets|presentation|forms)\/d\/([A-Za-z0-9_-]+)/.exec(u.pathname);
  if (m) return m[1];
  const q = u.searchParams.get('id');
  return q && /^[A-Za-z0-9_-]+$/.test(q) && (u.pathname.startsWith('/open') || u.pathname.startsWith('/uc')) ? q : null;
}

export interface KnownTaskFile { kind: TaskFileKind; id: number | string; name?: string | null; driveId: string }

/**
 * Split instruction text into plain text and links. Only Drive links whose
 * file id belongs to this task's own files become proxy links; every other
 * link stays plain text exactly as typed.
 */
export function splitInstructions(
  text: string, known: KnownTaskFile[],
): (string | { text: string; file: KnownTaskFile })[] {
  const out: (string | { text: string; file: KnownTaskFile })[] = [];
  let last = 0;
  for (const m of text.matchAll(/https:\/\/(?:drive|docs)\.google\.com\/[^\s<>"')]+/g)) {
    const id = driveIdOf(m[0]);
    const file = id ? known.find((k) => k.driveId === id) : undefined;
    if (!file) continue;
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push({ text: m[0], file });
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
